import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import PawPrint from "@/components/PawPrint";

const EMERGENCY_TIPS = [
  { icon: "\uD83E\uDE78", title: "Saignement abondant", text: "Appliquez une pression ferme avec un linge propre et rendez-vous aux urgences." },
  { icon: "\uD83E\uDD2E", title: "Ingestion toxique", text: "Ne faites PAS vomir. Notez le produit et la quantit\u00e9, appelez un v\u00e9t\u00e9rinaire imm\u00e9diatement." },
  { icon: "\uD83D\uDCA8", title: "Difficult\u00e9 respiratoire", text: "Gardez l'animal calme, a\u00e9rez la pi\u00e8ce et contactez les urgences v\u00e9t\u00e9rinaires." },
  { icon: "\u26A0\uFE0F", title: "Convulsions", text: "\u00c9loignez les objets dangereux, ne touchez pas la gueule. Chronom\u00e9trez la dur\u00e9e et appelez un v\u00e9t\u00e9rinaire." },
  { icon: "\uD83C\uDF21\uFE0F", title: "Fi\u00e8vre / hyperthermie", text: "Temp\u00e9rature > 39.5\u00b0C = urgence. Rafra\u00eechissez avec un linge humide et consultez." },
];

export default function UrgencesPage() {
  const navigate = useNavigate();
  const [location, setLocation] = useState(null);
  const [locError, setLocError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocError("La g\u00e9olocalisation n'est pas support\u00e9e par votre navigateur.");
      setLoading(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLoading(false);
      },
      () => {
        setLocError("Impossible d'obtenir votre position. V\u00e9rifiez les permissions.");
        setLoading(false);
      },
      { timeout: 10000 }
    );
  }, []);

  const openMaps = () => {
    if (location) {
      window.open(
        `https://www.google.com/maps/search/v\u00e9t\u00e9rinaire+urgence/@${location.lat},${location.lng},13z`,
        "_blank"
      );
    } else {
      window.open(
        "https://www.google.com/maps/search/v\u00e9t\u00e9rinaire+urgence",
        "_blank"
      );
    }
  };

  return (
    <div className="min-h-screen bg-app-gradient animate-fade-in">
      {/* Header */}
      <div
        className="px-6 py-5 flex items-center gap-3 shadow-pawcare"
        style={{ background: "linear-gradient(135deg, #FF7055 0%, #E53535 100%)", paddingTop: "max(20px, env(safe-area-inset-top, 20px))" }}
      >
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-white/20 border-none text-white text-lg flex items-center justify-center cursor-pointer"
        >
          {"\u2190"}
        </button>
        <PawPrint size={28} color="rgba(255,255,255,0.92)" />
        <div>
          <div className="font-display text-xl text-white leading-none">Urgences</div>
          <div className="text-[11px] text-white/75 font-semibold">Vétérinaires à proximité</div>
        </div>
      </div>

      <div className="px-5 pt-5 max-w-[540px] mx-auto">
        {/* CTA principal */}
        <div className="bg-white rounded-4xl p-5 shadow-[0_4px_20px_rgba(229,53,53,0.15)] border-[1.5px] border-red-200 mb-5">
          <div className="text-center mb-4">
            <div className="text-4xl mb-2">{"\uD83D\uDEA8"}</div>
            <h2 className="font-black text-lg text-app-text mb-1">Trouver un vétérinaire d'urgence</h2>
            <p className="text-sm text-app-muted font-semibold">
              {loading
                ? "Localisation en cours..."
                : locError
                  ? locError
                  : "Position détectée — recherche prête"}
            </p>
          </div>

          <button
            onClick={openMaps}
            className={cn(
              "w-full py-4 rounded-[18px] border-none cursor-pointer",
              "text-white font-black text-base",
              "flex items-center justify-center gap-2.5",
              "transition-all duration-200",
              "hover:scale-[1.02] active:scale-[0.98]",
            )}
            style={{ background: "linear-gradient(135deg, #FF7055, #E53535)", boxShadow: "0 6px 24px rgba(229,53,53,0.4)" }}
          >
            <span className="text-xl">{"\uD83D\uDDFA\uFE0F"}</span>
            Ouvrir la carte
            <span className="text-lg">{"\u2192"}</span>
          </button>
        </div>

        {/* Num\u00e9ros d'urgence */}
        <div className="bg-white rounded-3xl p-4 shadow-soft border-[1.5px] border-black/5 mb-5">
          <h3 className="font-black text-sm text-app-text mb-3">{"\uD83D\uDCDE"} Numéros utiles</h3>
          <div className="flex flex-col gap-2">
            {[
              { label: "Centre antipoison animal", number: "01 48 93 13 00", note: "CAPAE Alfort \u2014 24h/24" },
              { label: "Urgences v\u00e9t\u00e9rinaires", number: "3115", note: "Redirige vers le v\u00e9t\u00e9rinaire de garde" },
            ].map((item, i) => (
              <a
                key={i}
                href={`tel:${item.number.replace(/\s/g, "")}`}
                className="flex items-center gap-3 p-3 rounded-2xl bg-red-50 border-[1.5px] border-red-100 no-underline transition-all hover:bg-red-100"
              >
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-lg shadow-sm shrink-0">
                  {"\uD83D\uDCDE"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-[13px] text-app-text">{item.label}</div>
                  <div className="text-xs text-app-muted font-semibold">{item.note}</div>
                </div>
                <div className="font-black text-sm text-red-500 shrink-0">{item.number}</div>
              </a>
            ))}
          </div>
        </div>

        {/* Gestes de premiers secours */}
        <h3 className="font-black text-base text-app-text mb-3">{"\uD83E\uDE7A"} Premiers réflexes</h3>
        <div className="flex flex-col gap-2.5 mb-7">
          {EMERGENCY_TIPS.map((tip, i) => (
            <div
              key={i}
              className="bg-white rounded-3xl p-3.5 flex items-start gap-3 shadow-[0_2px_10px_rgba(0,0,0,0.05)] border-[1.5px] border-black/5"
              style={{ animation: `slideUp 0.4s cubic-bezier(0.34,1.56,0.64,1) ${i * 70}ms both` }}
            >
              <div className="w-10 h-10 rounded-[13px] bg-red-50 flex items-center justify-center text-xl shrink-0">
                {tip.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-black text-[13px] text-app-text mb-0.5">{tip.title}</div>
                <p className="text-[12.5px] text-gray-600 leading-normal m-0">{tip.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
